<script setup lang="ts">
import { computed } from 'vue';

import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useContext from '@/composables/useContext';

const { formatPrice, isAuthorized, channel } = useContext();
const { websiteText } = useWebsiteTexts();

const props = defineProps({
  isDecoration: {
    type: Boolean,
    required: true
  },
  item: {
    type: Object,
    required: true
  },
  giftCardFreightPrice: {
    type: Number,
    default: 0
  },
  isGiftCard: {
    type: Boolean
  }
});

const itemPrice = computed(() => {
  var price =
    props.item.price ?? props.item.currentPrice ?? props.item.originalPrice;
  if (
    channel.value.combineGiftCardPriceWithShipping &&
    props.isGiftCard &&
    !isAuthorized()
  ) {
    price += props.giftCardFreightPrice;
  }
  return price;
});

const pricePerItemText = computed(() => {
  return `(${formatPrice(itemPrice.value)}/${
    websiteText('productdetailspage_printpieceabbreviated').value
  })`;
});

const itemQuantityText = computed(() => {
  return `${props.item.quantity} ${
    websiteText('productdetailspage_printpieceabbreviated').value
  }`;
});

const totalRowPrice = computed(() => {
  return itemPrice.value * props.item.quantity;
});
</script>

<template>
  <div class="config-summary-row">
    <div class="config-summary-row__item-details">
      <div
        :class="[
          'config-summary-row__item-name',
          { 'config-summary-row__item-decoration': isDecoration }
        ]"
      >
        {{ item.name }}
      </div>
      <div
        v-if="item.campaignPercentage"
        class="config-summary-row__item-campaign"
      >
        (-{{ item.campaignPercentage }}%)
      </div>
      <div v-if="!isDecoration" class="config-summary-row__item-spacer"></div>
      <div class="config-summary-row__item-article-number" v-if="!isDecoration">
        {{ item.articleNumberForDisplay }}
      </div>
    </div>
    <template v-if="isDecoration">
      <div
        class="config-summary-row__item-fee"
        v-for="addon in item.addons"
        :key="addon.name"
      >
        <div class="config-summary-row__item-fee-name">
          {{ addon.name }}
        </div>
        <div class="config-summary-row__item-fee-price">
          {{ formatPrice(addon.price) }}
        </div>
      </div>
    </template>
    <div
      :class="[
        'config-summary-row__item-cost',
        { 'config-summary-row__item-product-indented': !isDecoration }
      ]"
    >
      <div class="config-summary-row__item-quantity">
        {{ itemQuantityText }} {{ pricePerItemText }}
      </div>
      <div class="config-summary-row__item-price">
        {{ formatPrice(totalRowPrice) }}
      </div>
    </div>
  </div>
</template>

<style>
.config-summary-row {
  color: var(--color-text-darker);
  padding-left: 10px;
  font-size: 12px;
  line-height: 17px;
}

.config-summary-row__item-details {
  display: flex;
  align-items: center;
}

.config-summary-row__item-name {
  margin-right: 4px;
  font-weight: 700;
}

.config-summary-row__item-campaign {
  color: var(--color-feedback-error);
  margin-left: 4px;
}

.config-summary-row__item-spacer {
  margin: 0 6px;
  height: 12px;
  border-left: 1px solid #e5e5e5;
}

.config-summary-row__item-fee,
.config-summary-row__item-cost {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.config-summary-row__item-decoration {
  font-style: italic;
  font-weight: normal;
  white-space: pre-wrap;
}

.config-summary-row__item-product-indented {
  padding-left: 10px;
}
</style>
